import React, { useMemo } from 'react';
import { App, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import { PRIMARY_DARK, getPrimaryDark } from '../../constants/constants';
import { emptyUrl, updateUrl } from '../../utils/browser';
import { useQueryDocumentTypes } from '../../hooks';
import { fetchProposal } from '../../requests/proposals';
import { SECTION_ACCENTS } from '../../shared/ui/editorAccents';
import GenerateDivider from '../common/GenerateDivider';
import { SectionTag } from '../RightSide/DocumentsFolderView';
import { flushPendingSaves } from '../../collab/flushPendingSaves';

// Contenu de l'onglet "Documents" du panel de gauche : ouvre le même modal
// de sélection de document qu'avant le déplacement (TemplateModal, monté
// dans LeftSide.jsx pour rester actif quel que soit l'onglet affiché).
// Inclut aussi les métadonnées du modèle (ex-onglet "Informations", fusionné
// ici) : n'existent que pour un modèle (currentTemplate), pas de date de
// création (le backend ne l'expose pas côté frontend).
// Libellés tracés comme les séparateurs de groupe des onglets Éléments et
// Blocs (GenerateDivider subtle), pour un seul style de séparateur dans le
// panel. Une couleur fixe par libellé, indépendante de la langue : le registre
// de editorAccents se lit par libellé français.
const LABEL_COLORS = {
  name: SECTION_ACCENTS.blue,
  format: SECTION_ACCENTS.teal,
  work: SECTION_ACCENTS.violet,
  tags: SECTION_ACCENTS.amber,
  recent: SECTION_ACCENTS.rose,
};

const PanelLabel = ({ color, children }) => (
  <GenerateDivider text={children} fullRow subtle accent={color} />
);

const InfoRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <PanelLabel color={color}>{label}</PanelLabel>
    <div style={{ color: PRIMARY_DARK }}>{value}</div>
  </div>
);

const QUICK_ACCESS_LIMIT = 8;

const DocumentsPanel = ({ onOpenDocuments, onCreateTemplate, isAdmin }) => {
  const { t } = useTranslation();
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const currentTemplate = useStore(({ currentTemplate }) => currentTemplate);
  const templates = useStore(({ templates }) => templates);
  const sections = useStore(({ sections }) => sections);
  const applyTemplate = useStore(({ applyTemplate }) => applyTemplate);
  const proposals = useStore(({ proposals }) => proposals);
  const currentProposal = useStore(({ currentProposal }) => currentProposal);
  const applyProposal = useStore(({ applyProposal }) => applyProposal);
  const setProposalId = useStore(({ setProposalId }) => setProposalId);
  const defaultVariablesApi = useStore(({ defaultVariablesApi }) => defaultVariablesApi);
  const handleUnSelectBox = useStore(({ handleUnSelectBox }) => handleUnSelectBox);
  const handleUnSelectContainer = useStore(({ handleUnSelectContainer }) => handleUnSelectContainer);
  const handleProposalList = useStore(({ handleProposalList }) => handleProposalList);
  const fetchDatas = useStore(({ fetchDatas }) => fetchDatas);
  const getTemplates = useStore(({ getTemplates }) => getTemplates);
  const { data: documentTypes } = useQueryDocumentTypes();
  const { modal } = App.useApp();

  // Même bleu que le token antd colorPrimary (cf. Layout.jsx) qui pilote le
  // Switch et les Collapse : mode-dépendant (navy proposal / violet template).
  const accentColor = getPrimaryDark(currentDocument.type === 'template');

  const tagNames = documentTypes
    ?.filter((dT) => currentTemplate?.document_type_ids?.includes(dT.id))
    .map((dT) => dT.name)
    .join(', ') || '-';

  // Accès rapide : liste courte pour changer de document sans repasser par la
  // modale/liste complète. Source et critère différents selon le contexte :
  // - mode template (authoring) : les modèles et sections (récursives ou non)
  //   récemment modifiés, mélangés dans une seule liste (updated_at,
  //   ajouté à l'endpoint /proposals/templates/ côté backend pour cet usage —
  //   cf. templates_controller.rb#index_template_json). Le classement par
  //   dossier (folder_id) a été essayé en premier mais donne une liste sans
  //   rapport visible dès que le modèle ouvert est à la racine (pas de
  //   dossier), ce qui est le cas courant : la récence est un signal plus
  //   fiable de "en quoi ça peut m'intéresser maintenant".
  // - mode proposal/offer : PAS les modèles (une opportunité/offre ne change
  //   pas souvent de modèle) mais les autres documents (propositions) déjà
  //   créés pour cette même opportunité/offre — même source que la table
  //   "Liste des documents" (ProposalList.jsx, state.proposals), triée par
  //   updated_at. currentProposal.id permet d'exclure celui déjà ouvert (ici
  //   ça marche, contrairement à currentTemplate en mode template : chaque
  //   proposal a un id propre tracké dans le store dès son chargement).
  const quickAccessItems = useMemo(() => {
    if (currentDocument.type === 'template') {
      return [...(templates ?? []), ...(sections ?? [])]
        .filter((tpl) => tpl.id !== currentTemplate?.id)
        .sort((a, b) => new Date(b.updated_at ?? 0) - new Date(a.updated_at ?? 0))
        .slice(0, QUICK_ACCESS_LIMIT);
    }
    if (!proposals?.length) return [];
    return [...proposals]
      .filter((p) => p.id !== currentProposal?.id)
      .sort((a, b) => new Date(b.updated_at ?? 0) - new Date(a.updated_at ?? 0))
      .slice(0, QUICK_ACCESS_LIMIT);
  }, [templates, sections, currentTemplate, proposals, currentProposal, currentDocument.type]);

  // Quitter le document courant : ce qui attend encore (frappe, diffs
  // débouncés) part d'abord. On ne prévient que si une sauvegarde a échoué —
  // l'ancien avertissement systématique s'affichait même tout enregistré.
  const leaveDocument = async (navigate) => {
    if (await flushPendingSaves()) {
      navigate();
      return;
    }
    modal.confirm({
      title: "Certaines modifications n'ont pas pu être enregistrées",
      content: 'Si vous quittez ce document maintenant, elles seront perdues.',
      okText: 'Quitter quand même',
      cancelText: 'Rester',
      autoFocusButton: 'cancel',
      onOk: navigate,
    });
  };

  const openProposalList = () => {
    handleProposalList(true);
    emptyUrl();
    if (currentDocument.type === 'opportunity') {
      updateUrl({ oid: currentDocument.id });
    } else if (currentDocument.type === 'offer') {
      updateUrl({ ofid: currentDocument.id });
      fetchDatas();
    }
  };

  const switchToTemplate = (id) => {
    handleUnSelectBox();
    handleUnSelectContainer();
    updateUrl({ tid: id });
    applyTemplate(id);
  };

  // Même séquence que le clic sur une ligne de ProposalList.jsx : recharge le
  // document complet (fetchProposal) puis remplace le contenu de l'éditeur
  // (applyProposal). Pas de message de confirmation après coup (contrairement
  // à switchToTemplate) : le document chargé est déjà sauvegardé tel quel,
  // rien n'est "modifié en mémoire" ici.
  const switchToProposal = (id) => {
    handleUnSelectBox();
    handleUnSelectContainer();
    emptyUrl();
    if (currentDocument.type === 'opportunity') {
      updateUrl({ oid: currentDocument.id, pid: id });
    } else if (currentDocument.type === 'offer') {
      updateUrl({ ofid: currentDocument.id, pid: id });
    }
    setProposalId(id);
    Promise.resolve(
      fetchProposal({ id, type: currentDocument.type }, defaultVariablesApi)
    ).then((fetchedProposal) => {
      applyProposal(fetchedProposal, currentDocument.type, fetchedProposal.sections);
    });
  };

  // Cmd + clic (Ctrl + clic hors macOS) sur un document de la liste : il s'ouvre
  // dans un nouvel onglet, comme un lien, et celui-ci reste tel quel. Branché en
  // capture : en stoppant ici, ni le onClick de la ligne ni le Popconfirm qui
  // l'enrobe en proposition ne voient le clic (rien à perdre, ce document reste
  // ouvert). Mêmes paramètres d'URL que switchToTemplate / switchToProposal.
  const handleNewTabClick = (event, id) => {
    if (!(event.metaKey || event.ctrlKey)) return;
    event.stopPropagation();
    const url = new URL(window.location.href);
    if (currentDocument.type === 'template') {
      url.searchParams.set('tid', id);
    } else {
      url.searchParams.set(currentDocument.type === 'offer' ? 'ofid' : 'oid', currentDocument.id);
      url.searchParams.set('pid', id);
    }
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const actionButtonStyle = {
    color: accentColor,
    borderColor: accentColor,
    '--btn-accent-color': accentColor,
    fontSize: 13,
  };

  const documentsButton = currentDocument.type === 'template' ? (
    <Button
      className='style_btn_right_side'
      style={actionButtonStyle}
      icon={<FontAwesomeIcon icon={icon({ name: 'folder-open', style: 'light' })} style={{ marginRight: 8 }} />}
      onClick={() => {
        handleUnSelectBox();
        handleUnSelectContainer();
        onOpenDocuments();
        getTemplates();
      }}
      ghost
    >
      {t('documents')}
    </Button>
  ) : (
    <Button
      className='style_btn_right_side'
      style={actionButtonStyle}
      icon={<FontAwesomeIcon icon={icon({ name: 'folder-open', style: 'light' })} style={{ marginRight: 8 }} />}
      onClick={() => leaveDocument(openProposalList)}
      ghost
    >
      {t('documents')}
    </Button>
  );

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Les deux actions ouvrent le panneau. Posées sous les infos du modèle
          et la liste d'accès rapide, toutes deux de longueur variable, elles
          n'étaient jamais au même endroit d'un document à l'autre — et hors
          de vue dès que la liste est pleine. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {documentsButton}
        {isAdmin && currentDocument.type === 'template' && onCreateTemplate && (
          <Button
            className='style_btn_right_side'
            style={actionButtonStyle}
            icon={<FontAwesomeIcon icon={icon({ name: 'file-plus', style: 'light' })} style={{ marginRight: 8 }} />}
            onClick={() => onCreateTemplate(false)}
            ghost
          >
            {t('new-template')}
          </Button>
        )}
      </div>
      {currentDocument.type === 'template' && (
        // fontSize 11 : override le 9px hérité du panel gauche (cf. LeftSide.jsx),
        // ces infos doivent rester lisibles à la taille normale des panels.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 11 }}>
          <InfoRow label={t('info-name')} color={LABEL_COLORS.name} value={currentTemplate?.name || '-'} />
          <InfoRow
            label={t('info-format')}
            color={LABEL_COLORS.format}
            value={currentTemplate?.landscape ? t('info-format-landscape') : t('info-format-portrait')}
          />
          <InfoRow
            label={t('info-work')}
            color={LABEL_COLORS.work}
            value={currentTemplate?.work ? t(currentTemplate.work) : '-'}
          />
          <InfoRow label={t('info-tags')} color={LABEL_COLORS.tags} value={tagNames} />
        </div>
      )}
      {quickAccessItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <PanelLabel color={LABEL_COLORS.recent}>{t('quick-access-recent')}</PanelLabel>
          {quickAccessItems.map((item) => {
            const row = (
              <Button
                type='text'
                block
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  height: 'auto',
                  padding: '4px 8px',
                  color: PRIMARY_DARK,
                }}
                icon={<FontAwesomeIcon icon={icon({ name: 'file-lines', style: 'light' })} style={{ marginRight: 8 }} />}
                onClick={() => leaveDocument(() => (
                  currentDocument.type === 'template'
                    ? switchToTemplate(item.id)
                    : switchToProposal(item.id)
                ))}
                onClickCapture={(event) => handleNewTabClick(event, item.id)}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </span>
                <SectionTag item={item} style={{ marginLeft: 'auto', marginInlineEnd: 0, flexShrink: 0 }} />
              </Button>
            );
            return <React.Fragment key={item.id}>{row}</React.Fragment>;
          })}
        </div>
      )}
    </div>
  );
};

export default DocumentsPanel;
